import { z } from "zod";
import { DOCUMENT_EVIDENCE_TEMPLATE_CAPABILITY_VERSION, FRAMEWORK_TEMPLATE_CONTRACT_VERSION, FRAMEWORK_TEMPLATE_NODE_TYPES, } from "./contracts.js";
import { normalizeFrameworkIconKey } from "./icon-keys.js";
const nonEmptyStringSchema = z.string().min(1);
/**
 * A framework icon key, constrained to the shared `FRAMEWORK_ICON_KEYS` catalog.
 * Unknown or absent keys normalize to `null` rather than throwing, so a legacy or
 * mistyped key never fails a whole compile — it simply resolves to no icon (the
 * same outcome the renderer falls back to). New writes are additionally gated by
 * the icon picker + the entity-type write path, so bad keys can't enter here.
 */
export const frameworkIconKeySchema = z
    .union([z.string(), z.null()])
    .optional()
    .transform((value) => normalizeFrameworkIconKey(value ?? null));
export const parentRefSchema = z.custom((value) => typeof value === "string" && value.startsWith("$parent."), "must be a '$parent.<alias>...' reference");
export const compiledFilterNodeSchema = z.lazy(() => z.discriminatedUnion("op", [
    z.object({
        op: z.literal("and"),
        nodes: z.array(compiledFilterNodeSchema),
    }),
    z.object({
        op: z.literal("or"),
        nodes: z.array(compiledFilterNodeSchema),
    }),
    z.object({
        op: z.literal("not"),
        node: compiledFilterNodeSchema,
    }),
    z.object({
        op: z.literal("eq"),
        field: nonEmptyStringSchema,
        value: z.unknown().optional(),
        valueFrom: parentRefSchema.optional(),
    }),
    z.object({
        op: z.literal("neq"),
        field: nonEmptyStringSchema,
        value: z.unknown().optional(),
        valueFrom: parentRefSchema.optional(),
    }),
    z.object({
        op: z.literal("in"),
        field: nonEmptyStringSchema,
        values: z.array(z.unknown()).optional(),
        valuesFrom: parentRefSchema.optional(),
    }),
    z.object({
        op: z.literal("exists"),
        field: nonEmptyStringSchema,
        value: z.boolean(),
    }),
    z.object({
        op: z.union([z.literal("gt"), z.literal("gte"), z.literal("lt"), z.literal("lte")]),
        field: nonEmptyStringSchema,
        value: z.number(),
    }),
]));
export const fromParentSchema = z.object({
    alias: nonEmptyStringSchema,
    entityTypeKey: nonEmptyStringSchema,
});
export const createActionParentBindingSchema = z.object({
    alias: nonEmptyStringSchema,
    entityTypeKey: nonEmptyStringSchema,
    targetField: nonEmptyStringSchema.optional(),
});
export const createActionSchema = z.object({
    presetKey: nonEmptyStringSchema,
    targetEntityTypeKey: nonEmptyStringSchema,
    label: nonEmptyStringSchema,
    iconKey: frameworkIconKeySchema,
    defaultValues: z.record(z.string(), z.unknown()).optional(),
    parentBinding: createActionParentBindingSchema.optional(),
});
export const projectTreeFolderManagedPlacementSchema = z.object({
    targetEntityTypeKey: nonEmptyStringSchema,
    systemKey: nonEmptyStringSchema,
    parentFrameworkNodeKey: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
});
export const folderNodeSettingsProjectTreeFoldersSchema = z.object({
    enabled: z.boolean(),
    kind: z.string().optional(),
    managedPlacements: z.array(projectTreeFolderManagedPlacementSchema).optional(),
});
export const folderNodeSettingsSchema = z.object({
    alwaysOpen: z.boolean().optional(),
    displayMode: z.literal("mixed").optional(),
    hideChevron: z.boolean().optional(),
    projectTreeFolders: folderNodeSettingsProjectTreeFoldersSchema.optional(),
});
// A key belongs here only when it has both a runtime consumer and an admin
// surface. Strictness prevents silent stripping and hidden configuration drift.
export const viewTabConfigSchema = z
    .object({
    label: z.string().optional(),
    ownerFieldId: z.string().nullable().optional(),
})
    .strict();
export const templateFieldSchema = z.object({
    nodeType: z.enum(FRAMEWORK_TEMPLATE_NODE_TYPES),
    fieldId: nonEmptyStringSchema,
    persistenceKey: z.string().optional(),
    label: nonEmptyStringSchema,
    placeholder: z.string().optional(),
    required: z.boolean().optional(),
    guidance: z.string().optional(),
    assistantGuidance: z.string().optional(),
    expectedMentionTypes: z.array(z.string()).optional(),
});
export const templateTabSchema = z.object({
    contentExpression: nonEmptyStringSchema,
    fields: z.array(templateFieldSchema),
});
/**
 * Per-attribute prose snapshot embedded in a template version's source. Additive
 * and optional: zod strips unknown keys, so this must be declared here for the
 * field to round-trip through `parseTemplateSourceSafe` and the draft save instead
 * of being silently dropped. The compiler still ignores it (`parseTemplateSource`
 * in `frameworks.ts` reconstructs the compiled output without it).
 */
const templateAttributeProseSchema = z.object({
    assistantGuidance: z.string().optional(),
    guidance: z.string().optional(),
    optionDescriptions: z.record(z.string(), z.string()).optional(),
});
export const templateSourceSchema = z.object({
    contractVersion: z.literal(FRAMEWORK_TEMPLATE_CONTRACT_VERSION),
    documentEvidenceVersion: z.literal(DOCUMENT_EVIDENCE_TEMPLATE_CAPABILITY_VERSION).optional(),
    purpose: z.string().optional(),
    guidance: z.string().optional(),
    assistantGuidance: z.string().optional(),
    tabs: z.record(z.string(), templateTabSchema),
    attributeProse: z.record(z.string(), templateAttributeProseSchema).optional(),
});
export function formatFrameworkConfigSchemaError(error) {
    return error.issues
        .map((issue) => {
        const issuePath = issue.path.length > 0 ? `.${issue.path.join(".")}` : "";
        return `${issuePath} ${issue.message}`.trim();
    })
        .join("; ");
}
